import Dispatcher, { DispatcherManager } from '../../Dispatcher';
import { Action, OperationType, Position } from '../../Types';
import { Service } from '../Service';
import {
  DispatcherPopupWindow,
  iPopupWindow,
  WindowCoordinates
} from '../Types';
import _ from 'lodash';
import { spliceArray } from '../../../utility';

const WindowPosition = {
  previous: 'previous-screen',
  parent: 'center-parent',
  screen: 'center-screen'
};

class PopupWindow extends Service {
  private static _instance: PopupWindow;
  intervalID;
  /**
   * Remembers previous position of window and reopens it on same position if pos has value of WindowPosition.previous
   */
  private static previousPosition: {
    top?: number,
    left?: number,
    width?: number,
    height?: number
  } = {};

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  private popupWindow: iPopupWindow | null = null;
  private popupWindows: { [key: string]: iPopupWindow } = {};
  private windowCheckerInterval: Record<string, any> = {};
  // private released: boolean;
  private onCloseOperationsMap: { [key: string]: Action } = {};
  private dispatcherId: string;
  private static topDifference: number = 0;
  private onLoadFn: Record<string, any> = {};

  constructor() {
    super();
    this.registerServiceToDispatcher();
    window.addEventListener('message', e => this.executeActionInAnotherBrowserWindow(e));
  }

  public show = (cfg: any) => {
    let {
      actionName,
      name,
      children = null,
      data,
      placeholders = {},
      dispatcherId,
      height = 500,
      width = 900,
      pos = WindowPosition.screen,
      renderTo = null,
      title,
      onCloseOperations = [],
      windowId
    } = cfg;
    this.dispatcherId = dispatcherId;
    this.onCloseOperationsMap[windowId] = onCloseOperations;

    // Prepare position of the new window to be centered against the 'parent' window or 'screen'.

    let { left, top } = this.getCoordinates(pos, width, height);

    let updateContent: boolean = false;
    if (pos === WindowPosition.previous) {
      width = PopupWindow.previousPosition.width || width;
      height = PopupWindow.previousPosition.height || height;
    }
    PopupWindow.previousPosition.width = width;
    PopupWindow.previousPosition.height = height;

    this.popupWindow = this.popupWindows[windowId];
    if (this.popupWindow && !this.popupWindow.closed) {
      updateContent = true;
    } else {
      let features = `width=${width},height=${height},left=${left},top=${top},menubar=no,resizable=no,scrollbars=no,status=no,location=no,popup=yes`;
      // @ts-ignore
      this.popupWindow = window.open(window.location.href, name, features);
      this.popupWindows[windowId] = this.popupWindow;

      this.onLoadFn[windowId] = this.onLoad.bind(this, top, windowId, title, placeholders);
      this.popupWindow?.addEventListener('load', this.onLoadFn[windowId]);

      // @ts-ignore
      window.popupWindowService = this;
    }

    if (this.popupWindow) {
      this.popupWindow.moveTo(left, top);
      this.popupWindow.resizeTo(width, height);
      this.popupWindow.focus();

      this.popupWindow.dispatcherPopupWindowData = {
        children,
        operation: cfg.appOperationData,
        persistent: cfg.appAllPersistentData,
        dispatcherId: this.dispatcherId,
        renderTo,
        actionName,
        data
      } as DispatcherPopupWindow;

      if (!updateContent) {
        // this.released = false;

        // Release anything bound to this component before the new window unload.
        this.popupWindow.addEventListener('beforeunload', () => this.release(windowId));
        this.popupWindow.addEventListener('keydown', e => this.onKeyDown(e));
        if (pos === WindowPosition.previous) {
          this.popupWindow.addEventListener('resize', (e: any) => this.onResize(e));
          this.popupWindow.addEventListener('mouseout', (e: any) => this.onMouseOut(e));
        }
      } else if (updateContent) {
        this.popupWindow.postMessage(
          {
            actionName,
            actionType: {
              dispatcherId,
              executeOnlyOperationType: OperationType.popupWindow,
              pos: Position.end
            },
            data: {
              ...data,
              redirectPopupWindow: true
            }
          },
          window.location.href,
          []
        );
      }
    } else {
      // Handle error on opening of new window.
      console.warn('A new window could not be opened. Maybe it was blocked.');
    }

    // When a new window use content from a cross-origin there's no way we can attach event
    // to it. Therefore, we need to detect in a interval when the new window was destroyed
    // or was closed.
    if (!updateContent && !this.windowCheckerInterval[windowId]) {
      this.windowCheckerInterval[windowId] = setInterval(() => this.periodicallyUpdate(), 200);
    }
  };

  periodicallyUpdate = () => {
    let windowId = null;
    Object.keys(this.popupWindows).every((prop) => {
      if (this.popupWindows[prop].closed) {
        windowId = prop;
        return false;
      } else {
        return true;
      }
    });
    if (!_.isNull(windowId)) {
      this.release(windowId);
    } else if (this.popupWindow && !this.popupWindow.closed) {
      this.saveCoordinatesAndSizeOfWindow(this.popupWindow);
    }
  };

  /**
   * Closes popup window.
   */
  private close(windowId?: string) {
    this.popupWindow = this.popupWindows[windowId];
    if (!_.isString(windowId) && !this.popupWindow && !_.isEmpty(this.popupWindows)) {
      windowId = Object.keys(this.popupWindows).pop();
      this.popupWindow = this.popupWindows[windowId];
    }
    if (this.popupWindow) {
      this.popupWindow.removeEventListener('keydown', e => this.onKeyDown(e));
      this.popupWindow.removeEventListener('resize', (e: any) => this.onResize(e));
      this.popupWindow.removeEventListener('mouseout', (e: any) => this.onMouseOut(e));
      this.popupWindow.removeEventListener('load', this.onLoadFn[windowId]);
      this.popupWindow.close();
      this.release(windowId);
    }
  }

  /**
   * Release the new window and anything that was bound to it.
   */
  private release(windowId?: string) {
    // This method can be called once.
    // if (!this.released) {
    //   this.released = true;
    if (!_.isEmpty(this.popupWindows)) {
      this.popupWindow = this.popupWindows[windowId];
      if (this.popupWindow && _.isFunction(this.popupWindow.removeEventListener)) {
        this.popupWindow.removeEventListener('focus', () => this.onFocus.bind(this, windowId));
        this.popupWindow.removeEventListener('keydown', e => this.onKeyDown(e));
        this.popupWindow.removeEventListener('resize', (e: any) => this.onResize(e));
        this.popupWindow.removeEventListener('mouseout', (e: any) => this.onMouseOut(e));
        this.popupWindow.removeEventListener('load', this.onLoadFn[windowId]);
      }
      if (this.onCloseOperationsMap[windowId].length) {
        const dispatcher = this.getDispatcher();
        this.onCloseOperationsMap[windowId].unshift({
          data: {
            closedWindowData: this.popupWindow.Dispatcher.appOperationData
          },
          type: OperationType.data
        });
        spliceArray(dispatcher.appExecutedOperations, this.onCloseOperationsMap[windowId], dispatcher.appExecutedOperations.length, 0);
        dispatcher.toDispatcher(true);
      }
      delete this.popupWindows[windowId];
      // Remove checker interval.
      if (_.isEmpty(this.popupWindows)) {
        this.popupWindow = null;
      } else {
        const props = Object.keys(this.popupWindows);
        this.popupWindow = this.popupWindows[props.pop()];
      }
      clearInterval(this.windowCheckerInterval[windowId]);
      delete this.windowCheckerInterval[windowId];
    }
  }

  /**
   * Gets coordinates.
   * @param pos
   * @param width
   * @param height
   */
  private getCoordinates = (pos: string, width: number, height: number): WindowCoordinates => {
    let left = 0;
    let top = 0;
    if (pos === WindowPosition.previous && !_.isNumber(PopupWindow.previousPosition?.left)) {
      // if position is WindowPosition.previous and coordinates are not defined, on initial show make window
      // centered by parent
      pos = WindowPosition.parent;
    }
    if (pos === WindowPosition.previous) {
      top = PopupWindow.previousPosition.top;
      left = PopupWindow.previousPosition.left;
    } else if (pos === WindowPosition.parent) {
      left = window.top.outerWidth / 2 + window.top.screenX - width / 2;
      top = window.top.outerHeight / 2 + window.top.screenY - height / 2;
    } else if (pos === WindowPosition.screen) {
      // @ts-ignore
      const screenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screen.left;
      // @ts-ignore
      const screenTop = window.screenTop !== undefined ? window.screenTop : window.screen.top;

      const innerWidth = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : window.screen.width;
      const innerHeight = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : window.screen.height;

      left = innerWidth / 2 - width / 2 + screenLeft;
      top = innerHeight / 2 - height / 2 + screenTop;
    }
    PopupWindow.previousPosition.left = left;
    PopupWindow.previousPosition.top = top;
    return { left, top };
  };

  /**
   * Getter for dispatcher.
   */
  private getDispatcher = (): DispatcherManager => Dispatcher.appDispatchers[this.dispatcherId] || Dispatcher;

  private onFocus = (windowId: string) => {
    this.popupWindow = this.popupWindows[windowId];
  }

  /**
   * Prevents child browser window to refresh (reload and hard reload).
   * @param e
   */
  private onKeyDown(e: KeyboardEvent) {
    const { ctrlKey = false, key } = e;
    switch (key) {
      case 'r':
      case 'R':
        if (ctrlKey) {
          e.preventDefault();
        }
        break;
      case 'F5':
        e.preventDefault();
        break;
      default:
        break;
    }
  }

  private onLoad = (top, windowId, title, placeholders) => {
    if (this.popupWindows[windowId]?.document) {
      this.popupWindows[windowId].document.title = this.translate(title, placeholders);
    }
    if (this.popupWindow && this.popupWindow.screenY !== top) {
      const topDifference = Math.abs(this.popupWindow.screenY - top);
      PopupWindow.topDifference = topDifference;
      if (topDifference > 1) {
        const correctedTop = top - topDifference;
        this.popupWindow.moveTo(PopupWindow.previousPosition.left, correctedTop);
      }
    }
  }

  /**
   * Executes action in child browser window.
   * @param e
   */
  private executeActionInAnotherBrowserWindow(e: MessageEvent) {
    if (e.data && e.data.actionName) {
      const { actionName, actionType, data } = e.data;
      this.dispatch(actionName, actionType, data);
    }
  }

  /**
   * Handles resizing of PopUp window. Save coordinates and new width and height after resize.
   * @param e
   */
  private onResize(e: any) {
    this.saveCoordinatesAndSizeOfWindow();
  }

  /**
   * Handles mouseout. When cursor leaves PopUp window, coordinates and new width and height will be saved.
   * @param evt
   */
  private onMouseOut(evt: any) {
    if (evt.toElement === null && evt.relatedTarget === null) {
      // if outside the window...
      this.saveCoordinatesAndSizeOfWindow(this.popupWindow);
    }
  }

  /**
   * For passed window extract coordinates and window size and save it to previousPosition.
   * @param window By default window will have value of PopupWindow.
   */
  private saveCoordinatesAndSizeOfWindow = (window: any = this.popupWindow) => {
    if (_.isNumber(window?.outerWidth) && window.outerWidth !== 0) {
      _.merge(PopupWindow.previousPosition, {
        left: window.screenX,
        top: window.screenY,
        width: window.outerWidth,
        height: window.outerHeight
      });
    }
  };
}

const PopupWindowService = PopupWindow.Instance;
export default PopupWindowService;
